import { Send, User, Bot, Loader2, Zap, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ChatMessage } from "../types";

interface ChatProps {
  messages: ChatMessage[];
  isAgentRunning: boolean;
  onSend: (content: string) => void;
  onCancel: () => void;
}

const SUGGESTED = [
  "What is Workers AI and how does it compare to other AI platforms?",
  "Explain Durable Objects with a real-world use case",
  "How does Cloudflare Workflows enable human-in-the-loop AI?",
  "Browse https://developers.cloudflare.com and summarize AI services",
];

export default function Chat({ messages, isAgentRunning, onSend, onCancel }: ChatProps) {
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  };

  return (
    <div className="flex flex-col h-full bg-dark-bg">
      {/* Panel label */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-dark-border">
        <span className="w-2 h-2 rounded-full bg-cf-orange shadow-[0_0_6px_#F6821F]" />
        <span className="text-[11px] font-mono font-semibold uppercase tracking-widest text-slate-500">
          Conversation
        </span>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
        {messages.length === 0 ? (
          <EmptyState onSuggestion={onSend} isRunning={isAgentRunning} />
        ) : (
          messages.map((msg, i) => <MessageBubble key={i} message={msg} />)
        )}
        {isAgentRunning && messages.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-dark-surface border border-dark-border max-w-xs animate-fade-in">
            <div className="w-7 h-7 flex-shrink-0 rounded-lg bg-cf-orange/10 border border-cf-orange/20 flex items-center justify-center">
              <Bot className="w-4 h-4 text-cf-orange" />
            </div>
            <div className="flex gap-1 items-center">
              {[0, 1, 2].map((k) => (
                <div
                  key={k}
                  className="w-1.5 h-1.5 rounded-full bg-cf-orange animate-bounce"
                  style={{ animationDelay: `${k * 0.15}s` }}
                />
              ))}
              <span className="ml-2 text-xs font-mono text-slate-500">processing…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-dark-border bg-dark-surface px-4 py-3">
        {isAgentRunning && (
          <button
            onClick={onCancel}
            className="flex items-center justify-center gap-2 w-full mb-2 py-1.5 rounded-lg border border-red-900/40 bg-red-950/20 text-red-400 text-xs font-mono hover:bg-red-950/40 hover:border-red-800/50 transition-all"
          >
            <Square className="w-3 h-3" />
            Stop generating
          </button>
        )}
        <div
          className="flex items-end gap-2 rounded-xl border px-3 py-2.5 transition-all border-dark-border-2 bg-dark-bg focus-within:border-cf-orange/50 focus-within:shadow-[0_0_0_1px_rgba(246,130,31,0.15)]"
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Ask the agent anything…"
            rows={1}
            className="flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-600 resize-none outline-none min-h-[24px] max-h-40 font-mono"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-cf-orange text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cf-orange-dark transition-all hover:shadow-glow-orange"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1.5 text-[10px] font-mono text-slate-600 text-center">
          ↵ send · shift+↵ newline
        </p>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex gap-2.5 animate-fade-in ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border ${
          isUser
            ? "bg-slate-700/50 border-slate-600"
            : "bg-cf-orange/10 border-cf-orange/20"
        }`}
      >
        {isUser ? (
          <User className="w-3.5 h-3.5 text-slate-400" />
        ) : (
          <Bot className="w-3.5 h-3.5 text-cf-orange" />
        )}
      </div>
      <div
        className={`max-w-[82%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-cf-orange text-white rounded-tr-sm"
            : "bg-dark-surface border border-dark-border text-slate-300 rounded-tl-sm"
        }`}
      >
        <p className="whitespace-pre-wrap break-words font-mono text-[13px]">{message.content}</p>
        {message.timestamp && (
          <p className={`text-[10px] mt-1.5 font-mono ${isUser ? "text-orange-200" : "text-slate-600"}`}>
            {new Date(message.timestamp).toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onSuggestion, isRunning }: { onSuggestion: (s: string) => void; isRunning: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-10 px-4 text-center">
      <div className="relative mb-4">
        <div className="w-16 h-16 rounded-2xl bg-cf-orange/10 border border-cf-orange/20 flex items-center justify-center animate-glow-pulse">
          <Zap className="w-8 h-8 text-cf-orange" />
        </div>
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-dark-bg shadow-[0_0_6px_#34d399]" />
      </div>
      <h3 className="text-base font-bold text-slate-200 mb-1">Cloudflare AI Agent</h3>
      <p className="text-xs font-mono text-slate-500 mb-6 max-w-xs leading-relaxed">
        Workers AI · Vectorize · Browser Rendering · R2 · Durable Objects · Workflows
      </p>
      <div className="grid grid-cols-1 gap-2 w-full max-w-sm">
        {SUGGESTED.map((s) => (
          <button
            key={s}
            onClick={() => !isRunning && onSuggestion(s)}
            disabled={isRunning}
            className="text-left text-xs font-mono text-slate-400 bg-dark-surface border border-dark-border rounded-lg px-3 py-2.5 hover:border-cf-orange/40 hover:text-cf-orange hover:bg-dark-surface-2 transition-all disabled:opacity-30 leading-relaxed"
          >
            <span className="text-cf-orange mr-1.5">›</span>{s}
          </button>
        ))}
      </div>
    </div>
  );
}
